import React, { useState, useEffect, useCallback } from 'react';
import { ENDPOINTS } from '../config/api';
import { TOSCheckbox } from './TermsOfService';

function SignupForm({ onSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [tosAccepted, setTosAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(ENDPOINTS.FIAT_PROVIDER_SIGNUP, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password, name, tos_accepted: tosAccepted }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Signup failed');
      onSuccess(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-md mx-auto">
      <h2 className="text-xl font-bold text-white mb-2">Become a Provider</h2>
      <p className="text-sm text-slate-400 mb-4">List your API, data feed, or tool on the THANE marketplace. No crypto knowledge required.</p>

      <input type="text" placeholder="Provider name (e.g., CoinGlass)" value={name} onChange={e => setName(e.target.value)}
        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500/50" required />
      <input type="email" placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)}
        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500/50" required />
      <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)}
        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500/50" required minLength={8} />

      {error && <p className="text-red-400 text-xs">{error}</p>}

      <TOSCheckbox accepted={tosAccepted} onChange={setTosAccepted} />

      <button type="submit" disabled={loading || !tosAccepted}
        className="w-full py-3 bg-purple-600 hover:bg-purple-500 disabled:bg-slate-700 disabled:text-slate-500 rounded-lg text-sm font-medium transition-colors">
        {loading ? 'Creating account...' : 'Sign Up — Free'}
      </button>

      <p className="text-[10px] text-slate-600 text-center">
        We create a secure wallet for you automatically. No crypto setup needed.
      </p>
    </form>
  );
}

function LoginForm({ onSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(ENDPOINTS.FIAT_PROVIDER_LOGIN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Login failed');
      onSuccess(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-md mx-auto">
      <h2 className="text-xl font-bold text-white mb-2">Provider Login</h2>
      <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)}
        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500/50" required />
      <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)}
        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500/50" required />
      {error && <p className="text-red-400 text-xs">{error}</p>}
      <button type="submit" disabled={loading}
        className="w-full py-3 bg-purple-600 hover:bg-purple-500 disabled:bg-slate-700 rounded-lg text-sm font-medium transition-colors">
        {loading ? 'Logging in...' : 'Log In'}
      </button>
    </form>
  );
}

function ProviderDashboard({ profile, onRefresh }) {
  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Provider Dashboard</h2>
        <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
          profile.on_chain ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
        }`}>
          {profile.on_chain ? 'Active on marketplace' : 'Not yet registered'}
        </span>
      </div>

      {/* Profile card */}
      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-[10px] text-slate-500 uppercase">Email</p>
            <p className="text-slate-300">{profile.email}</p>
          </div>
          <div>
            <p className="text-[10px] text-slate-500 uppercase">Wallet</p>
            <p className="text-slate-400 font-mono text-xs truncate">{profile.solana_wallet}</p>
          </div>
        </div>

        {profile.on_chain && (
          <>
            <div className="border-t border-slate-800 pt-3 grid grid-cols-3 gap-3 text-sm">
              <div>
                <p className="text-[10px] text-slate-500 uppercase">Name</p>
                <p className="text-white font-medium">{profile.name}</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-500 uppercase">Reputation</p>
                <p className="text-white">{profile.reputation_lifetime}<span className="text-slate-500">/100</span></p>
              </div>
              <div>
                <p className="text-[10px] text-slate-500 uppercase">Total Calls</p>
                <p className="text-white">{profile.total_calls?.toLocaleString()}</p>
              </div>
            </div>

            <div className="border-t border-slate-800 pt-3 flex items-center justify-between">
              <div>
                <p className="text-[10px] text-slate-500 uppercase">Claimable Earnings</p>
                <p className="text-2xl font-bold text-emerald-400">{profile.claimable_earnings_usd}</p>
              </div>
              <button
                onClick={async () => {
                  await fetch(ENDPOINTS.FIAT_PROVIDER_WITHDRAW, { method: 'POST', credentials: 'include' });
                  onRefresh();
                }}
                disabled={profile.claimable_earnings_usdc === 0}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-500 rounded-lg text-sm font-medium transition-colors"
              >
                Withdraw to Bank
              </button>
            </div>
          </>
        )}

        {!profile.on_chain && (
          <div className="border-t border-slate-800 pt-3 text-center">
            <p className="text-sm text-slate-400 mb-3">Your account is ready. Register your listing to start earning.</p>
            <p className="text-xs text-slate-500">Staking starts at $1.00 for Basic tier. Pay with credit card.</p>
          </div>
        )}
      </div>

      {/* Discovery links */}
      {profile.on_chain && (
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
          <h3 className="text-xs text-slate-500 uppercase tracking-wider mb-2">Discovery Profiles</h3>
          <p className="text-[10px] text-slate-400 mb-3">Your listing is discoverable via every major agent protocol:</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { label: 'A2A', desc: 'Google', path: 'agent.json' },
              { label: 'UCP', desc: 'Commerce', path: 'ucp.json' },
              { label: 'MCP', desc: 'Tools', path: 'mcp.json' },
              { label: 'DID', desc: 'Identity', path: 'did.json' },
            ].map(p => (
              <a key={p.label} href={`${ENDPOINTS.HEALTH.replace('/health', '')}/v1/provider/${profile.solana_wallet}/${p.path}`}
                target="_blank" rel="noreferrer"
                className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-2 text-center hover:border-purple-500/30 transition-colors">
                <p className="text-xs font-medium text-purple-400">{p.label}</p>
                <p className="text-[10px] text-slate-500">{p.desc}</p>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProviderPortal() {
  const [view, setView] = useState('signup'); // signup | login | dashboard
  const [profile, setProfile] = useState(null);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await fetch(ENDPOINTS.FIAT_PROVIDER_ME, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
        setView('dashboard');
      }
    } catch { /* not logged in */ }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  return (
    <div className="py-6">
      {view === 'dashboard' && profile ? (
        <ProviderDashboard profile={profile} onRefresh={fetchProfile} />
      ) : view === 'login' ? (
        <div>
          <LoginForm onSuccess={() => fetchProfile()} />
          <p className="text-center text-xs text-slate-500 mt-4">
            Don't have an account? <button onClick={() => setView('signup')} className="text-purple-400 hover:text-purple-300">Sign up</button>
          </p>
        </div>
      ) : (
        <div>
          <SignupForm onSuccess={() => fetchProfile()} />
          <p className="text-center text-xs text-slate-500 mt-4">
            Already have an account? <button onClick={() => setView('login')} className="text-purple-400 hover:text-purple-300">Log in</button>
          </p>
        </div>
      )}
    </div>
  );
}
